import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import React, { useState, useMemo, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {
  CustomTable,
  InputControl,
  CustomModal,
  UseUoMHelper,
} from "../../../shared";
import InfoIcon from "@mui/icons-material/Info";
import { createColumnHelper } from "@tanstack/react-table";
import { SelectGroupContent } from "./SelectGroupContent";
import { useStockCategoryQueries } from "../../StockCategoriesAndTags/hooks/useStockCategoryQueries";
import { useStockTagQueries } from "../../StockCategoriesAndTags/hooks/useStockTagQueries";
import { arraySort, formatCost, formatWeightWithUnits } from "../../../utils";
import { InfoRounded } from "@mui/icons-material";

export const AddStockItemForm = ({
  ingredientGroups = [],
  globalData,
  alternativeunitList = [],
  formData,
  handleFormChange,
  handleAddGroups,
  handleRemoveGroup,
  validator,
}) => {
  const [openSelectGroupModal, setOpenSelectGroupModal] = useState(false);
  const [tempSelectedGroups, setTempSelectedGroups] = useState([]);
  const { filterUnits, filterAlternativeUnits } = UseUoMHelper();

  // Fetch categories and tags
  const { listQuery: categoryListQuery } = useStockCategoryQueries({
    categorylist: true,
  });
  const { listQuery: tagListQuery } = useStockTagQueries();

  // Dropdown options
  const statusOptions = [
    { label: "Enabled", value: 1 },
    { label: "Disabled", value: 0 },
  ];

  // Transform categories data for dropdown
  const categoryOptions = useMemo(() => {
    if (!categoryListQuery.data) return [];
    return categoryListQuery.data.map((cat) => ({
      label: cat.name,
      value: cat.id,
      subData: cat.subData || [],
    }));
  }, [categoryListQuery.data]);

  // Get subcategories based on selected category
  const subCategoryOptions = useMemo(() => {
    if (!formData.stock_category_id) return [];
    const selectedCategory = categoryOptions.find(
      (cat) => cat.value === formData.stock_category_id,
    );
    if (!selectedCategory || !selectedCategory.subData) return [];
    return selectedCategory.subData.map((subCat) => ({
      label: subCat.name,
      value: subCat.id,
    }));
  }, [formData.stock_category_id, categoryOptions]);

  // Transform tags data for dropdown
  const tagOptions = useMemo(() => {
    if (!tagListQuery.data) return [];
    return tagListQuery.data.map((tag) => ({
      label: tag.name,
      value: tag.id,
    }));
  }, [tagListQuery.data]);

  // Transform globalData units for dropdown (used for base unit display only)
  const unitOptions = useMemo(() => {
    if (!globalData?.all_Units) return [];
    return globalData.all_Units.map((unit) => ({
      label: unit.label,
      value: unit.value,
    }));
  }, [globalData?.all_Units]);

  // Clear subcategory if category is deselected
  useEffect(() => {
    if (!formData.stock_category_id && formData.stock_subcategory_id) {
      handleFormChange("stock_subcategory_id", null);
    }
  }, [
    formData.stock_category_id,
    formData.stock_subcategory_id,
    handleFormChange,
  ]);

  // Calculate total weight from all groups
  const totalWeight = useMemo(() => {
    if (!formData.groups || formData.groups.length === 0) return "0.000";

    const sum = formData.groups.reduce((acc, group) => {
      // If group has boiling weight, use total_boiling_weight, otherwise use total_qty
      const weight =
        group.has_boiling_weight === 1
          ? parseFloat(group.total_boiling_weight || 0)
          : parseFloat(group.total_qty || 0);
      return acc + weight;
    }, 0);

    return sum.toFixed(3);
  }, [formData.groups]);

  // Calculate total cost from all groups
  const totalCost = useMemo(() => {
    if (!formData.groups || formData.groups.length === 0) return "0.00";

    const sum = formData.groups.reduce((acc, group) => {
      const cost = parseFloat(group.total_cost || 0);
      return acc + cost;
    }, 0);

    return formatCost(sum);
  }, [formData.groups]);

  // Handler to update alt_units array
  const handleAltUnitChange = (rowIndex, field, value) => {
    const updatedAltUnits = [...formData.alt_units];
    if (updatedAltUnits[rowIndex]) {
      updatedAltUnits[rowIndex] = {
        ...updatedAltUnits[rowIndex],
        [field]: value,
      };
      handleFormChange("alt_units", updatedAltUnits);
    }
  };

  // Cell component for Alternative Qty
  const AltTableAltQtyCell = ({ getValue, row }) => {
    const initialValue = getValue();
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const onBlur = () => {
      handleAltUnitChange(row.index, "alt_unit_qty", value);
    };

    return (
      <InputControl
        type="text"
        value={value || ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        size="small"
        required
        error={
          formData.is_alternate_UOM &&
          !!validator.current.message(
            "alternative qty",
            value,
            "required|numeric|min:0,num",
          )
        }
        helperText={
          formData.is_alternate_UOM &&
          validator.current.message(
            "alternative qty",
            value,
            "required|numeric|min:0,num",
          )
        }
        fullWidth
      />
    );
  };

  // Cell component for Alternative Unit
  const AltTableAltUnitCell = ({ getValue, row }) => {
    const initialValue = getValue();
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    // Same three-layer filtering as AddOrEditRecipeIngredient alt_unit_id cell
    let altUnitOptions = filterAlternativeUnits(
      filterUnits(alternativeunitList, ["ltr", "ml"]),
      row.index,
      formData.alt_units,
    );
    const mlId = alternativeunitList.find((i) => i.symbol === "ml")?.id;
    const ltrId = alternativeunitList.find((i) => i.symbol === "ltr")?.id;
    const hasMl = formData.alt_units.some(
      (item, i) => i !== row.index && item.alt_unit_id === mlId,
    );
    const hasLtr = formData.alt_units.some(
      (item, i) => i !== row.index && item.alt_unit_id === ltrId,
    );
    if (hasLtr) {
      altUnitOptions = altUnitOptions.filter((i) => i.id !== mlId);
    } else if (hasMl) {
      altUnitOptions = altUnitOptions.filter((i) => i.id !== ltrId);
    }

    const handleChange = (event, newValue) => {
      const unitValue = newValue?.id || null;
      setValue(unitValue);
      handleAltUnitChange(row.index, "alt_unit_id", unitValue);
    };

    return (
      <InputControl
        type="dropdown"
        options={arraySort(altUnitOptions || [], "name")}
        value={altUnitOptions.find((opt) => opt.id === value) || null}
        onChange={handleChange}
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            required
            error={
              formData.is_alternate_UOM &&
              !!validator.current.message("alternative unit", value, "required")
            }
            helperText={
              formData.is_alternate_UOM &&
              validator.current.message("alternative unit", value, "required")
            }
          />
        )}
        fullWidth
      />
    );
  };

  //alternative unit table

  const altcolumnHelper = createColumnHelper();
  const altcolumns = [
    altcolumnHelper.accessor("base_unit_qty", {
      header: () => "Base Qty",
      enableSorting: false,
      cell: ({ row }) => {
        return (
          <InputControl
            type="text"
            value={totalWeight}
            onChange={(e) => {
              // Handle change
              // console.log("Base Qty:", e.target.value);
            }}
            disabled={true}
            size="small"
            fullWidth
          />
        );
      },
    }),
    altcolumnHelper.accessor("base_unit_id", {
      header: () => "Base Unit",
      enableSorting: false,
      cell: ({ row }) => {
        return (
          <InputControl
            type="dropdown"
            options={unitOptions}
            value={unitOptions.find((opt) => opt.value === 4) || null}
            onChange={(event, newValue) => {
              // Base unit is always 4 (grams), no change allowed
              // console.log("Base Unit:", newValue);
            }}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            }
            renderInput={(params) => <TextField {...params} size="small" />}
            disabled={true}
            fullWidth
          />
        );
      },
    }),
    altcolumnHelper.accessor("alt_unit_qty", {
      header: () => "Alternative Qty",
      enableSorting: false,
      cell: AltTableAltQtyCell,
    }),
    altcolumnHelper.accessor("alt_unit_id", {
      header: () => "Alternative Unit",
      enableSorting: false,
      cell: AltTableAltUnitCell,
      meta: {
        minWidth: { xs: "100px", md: "150px", lg: "200px" },
      },
    }),
  ];

  // Get alternative units from formData
  const alt_units = formData.alt_units || [];

  // Get already added group IDs to disable them in the modal
  const alreadyAddedGroupIds = useMemo(() => {
    return formData.groups.map((group) => group.group_id);
  }, [formData.groups]);

  const handleOpenSelectGroup = () => {
    setOpenSelectGroupModal(true);
  };

  const handleCloseSelectGroup = () => {
    setTempSelectedGroups([]);
    setOpenSelectGroupModal(false);
  };

  const handleSaveSelectGroup = () => {
    // Filter ingredientGroups to get full data for selected groups
    const selectedGroupsData = ingredientGroups.filter((group) =>
      tempSelectedGroups.includes(group.group_id),
    );

    // Add selected groups to formData
    if (selectedGroupsData.length > 0) {
      handleAddGroups(selectedGroupsData);
    }

    // Clear temp selection and close modal
    setTempSelectedGroups([]);
    handleCloseSelectGroup();
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* First Row: Stock Item Name and Status */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <InputControl
          type="text"
          label="Stock Item Name"
          value={formData.stock_item_name}
          onChange={(e) => handleFormChange("stock_item_name", e.target.value)}
          size="small"
          sx={{ width: "70%" }}
          required
          error={
            !!validator.current.message(
              "stock item name",
              formData.stock_item_name,
              "required",
            )
          }
          helperText={validator.current.message(
            "stock item name",
            formData.stock_item_name,
            "required",
          )}
        />
        <InputControl
          type="dropdown"
          options={statusOptions}
          value={
            statusOptions.find((opt) => opt.value === formData.status) || null
          }
          onChange={(event, newValue) =>
            handleFormChange("status", newValue?.value)
          }
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Status"
              size="small"
              required
              error={
                !!validator.current.message(
                  "status",
                  formData.status,
                  "required",
                )
              }
              helperText={validator.current.message(
                "status",
                formData.status,
                "required",
              )}
            />
          )}
          sx={{ width: "30%" }}
        />
      </Stack>

      {/* Second Row: Stock Category, Stock Sub Category, Stock Item Tag */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Stack>
          <Stack width="100%" gap="5px">
            <InputControl
              name="stock_code"
              label="Stock Code"
              fullWidth
              size="small"
              value={formData?.stock_code}
              onChange={(event) =>
                handleFormChange("stock_code", event.target.value)
              }
              sx={{ width: "150px" }}
            />
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "flex", alignItems: "center", gap: "4px" }}
            >
              <InfoRounded fontSize="14px" />
              Used only for stocktake.
            </Typography>
          </Stack>
        </Stack>
        <InputControl
          type="dropdown"
          options={categoryOptions}
          value={
            categoryOptions.find(
              (opt) => opt.value === formData.stock_category_id,
            ) || null
          }
          onChange={(event, newValue) =>
            handleFormChange("stock_category_id", newValue?.value)
          }
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Stock Category"
              size="small"
              required
              error={
                !!validator.current.message(
                  "stock category",
                  formData.stock_category_id,
                  "required",
                )
              }
              helperText={validator.current.message(
                "stock category",
                formData.stock_category_id,
                "required",
              )}
            />
          )}
          fullWidth
        />
        <InputControl
          type="dropdown"
          options={subCategoryOptions}
          value={
            subCategoryOptions.find(
              (opt) => opt.value === formData.stock_subcategory_id,
            ) || null
          }
          onChange={(event, newValue) =>
            handleFormChange("stock_subcategory_id", newValue?.value)
          }
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Stock Sub Category"
              size="small"
              disabled={!formData.stock_category_id}
              required
              error={
                !!validator.current.message(
                  "stock sub category",
                  formData.stock_subcategory_id,
                  "required",
                )
              }
              helperText={validator.current.message(
                "stock sub category",
                formData.stock_subcategory_id,
                "required",
              )}
            />
          )}
          sx={{ minWidth: "200px" }}
          disabled={!formData.stock_category_id}
        />
        <InputControl
          type="dropdown"
          options={tagOptions}
          value={
            tagOptions.find((opt) => opt.value === formData.stock_tag_id) ||
            null
          }
          onChange={(event, newValue) =>
            handleFormChange("stock_tag_id", newValue?.value)
          }
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value === value?.value
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Stock Item Tag"
              size="small"
              // required
              // error={
              //   !!validator.current.message(
              //     "stock item tag",
              //     formData.stock_tag_id,
              //     "required",
              //   )
              // }
              // helperText={validator.current.message(
              //   "stock item tag",
              //   formData.stock_tag_id,
              //   "required",
              // )}
            />
          )}
          fullWidth
        />
      </Stack>

      {/* Dynamic Groups Section */}
      {formData.groups && formData.groups.length > 0 ? (
        formData.groups.map((group, index) => (
          <Stack
            key={index}
            sx={{
              backgroundColor: "#F5F8FC",
              padding: "8px",
              gap: "8px",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems={"center"}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "12px" }}>
                Group {index + 1}
              </Typography>
              <Stack direction={"row"} gap={1} alignItems={"center"}>
                <>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      color: "#485364",
                    }}
                  >
                    Group Total:{" "}
                    {formatWeightWithUnits(
                      group?.total_qty,
                      "kg",
                      globalData?.all_Units,
                    )}
                  </Typography>
                  {group.has_boiling_weight === 1 && (
                    <Typography sx={{ fontWeight: 600, fontSize: "12px" }}>
                      Group Boiled Weight:{" "}
                      {formatWeightWithUnits(
                        group?.total_boiling_weight,
                        "kg",
                        globalData?.all_Units,
                      )}
                    </Typography>
                  )}
                </>

                <RemoveCircleIcon
                  sx={{
                    color: "#B42318",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRemoveGroup(index)}
                />
              </Stack>
            </Stack>
            <Stack
              sx={{ backgroundColor: "#FFFFFF", padding: "8px", gap: "8px" }}
              direction={"row"}
              flexWrap="wrap"
            >
              {group.ingredients && group.ingredients.length > 0 ? (
                group.ingredients.map((ingredient, ingredientIndex) => (
                  <Chip
                    key={ingredientIndex}
                    size="small"
                    variant="outlined"
                    color="gray"
                    label={` ${ingredient.name}: ${ingredient.base_unit_qty} ${ingredient.base_unit_symbol}`}
                  />
                ))
              ) : (
                <Typography sx={{ fontSize: "12px", color: "#657083" }}>
                  No ingredients added
                </Typography>
              )}
            </Stack>
          </Stack>
        ))
      ) : (
        <Stack
          sx={{ backgroundColor: "#F5F8FC", padding: "8px", gap: "8px", mb: 2 }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#657083",
              textAlign: "center",
              py: 2,
            }}
          >
            No groups added yet
          </Typography>
        </Stack>
      )}

      {/* Select Group Button */}
      <Stack sx={{ backgroundColor: "#F5F8FC", padding: "8px" }}>
        <Button
          type="button"
          size="small"
          variant="text"
          sx={{ width: "135px" }}
          startIcon={<AddIcon />}
          onClick={handleOpenSelectGroup}
        >
          Select Group
        </Button>
      </Stack>

      {/* Cost and Weight Summary */}
      <Stack
        sx={{
          backgroundColor: "#1760D2",
          padding: "12px",
          gap: "16px",
          borderRadius: "4px",
          mb: 2,
        }}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <Typography
            sx={{ fontWeight: 500, fontSize: "14px", color: "#C7DDFF" }}
          >
            Cost Ex GST:
          </Typography>
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", color: "#C7DDFF" }}
          >
            ${totalCost}
          </Typography>
        </Stack>
        <Stack direction={"row"} gap={1} alignItems={"center"}>
          <Typography
            sx={{ fontWeight: 500, fontSize: "14px", color: "#FFFF" }}
          >
            {formData?.groups?.some((g) => g.has_boiling_weight === 1)
              ? "Finished Boiled Weight: "
              : "Total Weight: "}
          </Typography>
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", color: "#FFFF" }}
          >
            {totalWeight || 0} kg
          </Typography>
        </Stack>
      </Stack>

      {/* Alternate UoM Section */}
      <Stack sx={{ backgroundColor: "#F5F8FC", padding: "8px", gap: "8px" }}>
        <Stack direction={"row"} alignItems={"center"} sx={{ gap: "16px" }}>
          <Typography
            sx={{ fontWeight: 600, fontSize: "18px", textWrap: "nowrap" }}
          >
            Alternate UoM?
          </Typography>
          <InputControl
            type="switch"
            name="use_uom"
            onChange={(e) =>
              handleFormChange("is_alternate_UOM", e.target.checked)
            }
            checked={formData.is_alternate_UOM}
            disabled={false}
          />
          <Stack direction={"row"} alignItems={"center"}>
            <InfoIcon sx={{ height: "16px", color: "#485364" }} />
            <Typography
              sx={{ fontWeight: 400, fontSize: "14px", color: "#657083" }}
            >
              If provided, this Alternate unit will be used as the stocktake
              unit & kg will be used as Reference unit in Recipe App stocktake.
            </Typography>
          </Stack>
        </Stack>
        {validator.current.purgeFields()}
        {formData.is_alternate_UOM ? (
          <Stack>
            <CustomTable
              defaultData={alt_units ?? []}
              columns={altcolumns}
              // updateTableData={updateTableData}
              isPagination={false}
              isTableHeader={false}
            />
          </Stack>
        ) : (
          ""
        )}
      </Stack>

      <CustomModal
        open={openSelectGroupModal}
        handleClose={handleCloseSelectGroup}
        title="Select Group"
        onSubmit={handleSaveSelectGroup}
        isDisableSubmit={!tempSelectedGroups.length}
        PaperProps={{ style: { minWidth: "368px", minHeight: "498px" } }}
        content={
          <Box sx={{ p: "8px", backgroundColor: "#F7FAFC" }}>
            <SelectGroupContent
              ingredientGroups={ingredientGroups}
              globalData={globalData}
              selectedGroups={tempSelectedGroups}
              setSelectedGroups={setTempSelectedGroups}
              disabledGroupIds={alreadyAddedGroupIds}
            />
          </Box>
        }
      />
    </Box>
  );
};
